import { useState } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog from "@/v2-ui/dialog"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import { toast } from "@/v2-ui/toast"
import ActionMenu, {
  type ActionMenuItemProps,
} from "@/v2-backoffice-client/components/actionMenu"
import Button from "@/v2-backoffice-client/components/button"
import { useLicenseUpdateMutation } from "@/v2-backoffice-client/license/update/license.update.mutation"
import {
  dateInputToDatetime,
  evictLicenseQueries,
  getTrialFacts,
} from "@/v2-backoffice-client/license/license.utils"
import { LICENSE_STATUS_TRIAL } from "@/v2-backoffice-client/license/license.constants"
import type { LicenseFragment } from "@/v2-backoffice-client/license/__types__/license.fragment"

export type LicenseExtendMenuItemProps = Omit<
  ActionMenuItemProps,
  "onClick"
> & {
  license: LicenseFragment
}

// Extends (or shortens) a trial by moving its end date. The trial clock is
// the one field sales negotiates over, so it gets its own dialog instead of
// hiding in a generic edit form. Saving always (re)asserts Trial status, so
// the same action revives an already-expired trial when sales grants more
// time — a new date on a row still marked Expired would count down nothing.
function LicenseExtendMenuItem(props: LicenseExtendMenuItemProps) {
  const { license, children, ...menuItemProps } = props
  const [isOpen, setIsOpen] = useState(false)
  const [endsAt, setEndsAt] = useState(
    license.TrialEndsAt ? license.TrialEndsAt.slice(0, 10) : "",
  )

  const [updateLicense, { loading, error }] = useLicenseUpdateMutation({
    update: evictLicenseQueries,
    onCompleted(data) {
      const facts = getTrialFacts(data.updateLicense)
      toast({
        variant: "success",
        title: "Trial end date moved",
        description: facts
          ? `${data.updateLicense.CustomerName ?? "The customer"}'s trial now has ${facts.label}.`
          : undefined,
      })
      setIsOpen(false)
    },
  })

  return (
    <>
      <ActionMenu.Item
        iconSrc="calendar-clock"
        data-feature-code="UI_BackofficeLicense_Row_Extend"
        onSelect={(e) => {
          e.preventDefault()
          setIsOpen(true)
        }}
        {...menuItemProps}
      >
        {children ?? "Move Trial End Date"}
      </ActionMenu.Item>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <Dialog.Content align="top">
          <Dialog.Header>
            <Dialog.Title>Move Trial End Date</Dialog.Title>
            <Dialog.Description>
              Sets when {license.CustomerName ?? "this customer"}&apos;s{" "}
              {license.ProductName} trial ends. The change is audited with your
              user.
            </Dialog.Description>
          </Dialog.Header>

          <ErrorMessage className="my-4 text-center" error={error} />

          <div className="space-y-2">
            <Label htmlFor="licenseTrialEndsAt">Trial ends</Label>
            <Input
              id="licenseTrialEndsAt"
              type="date"
              required
              value={endsAt}
              onChange={(e) => {
                setEndsAt(e.target.value)
              }}
            />
          </div>

          <Dialog.Footer className="mt-6 flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsOpen(false)
              }}
            >
              Cancel
            </Button>

            <Button
              type="button"
              isLoading={loading}
              disabled={!endsAt}
              data-feature-code="UI_BackofficeLicense_Extend_Save"
              onClick={() => {
                updateLicense({
                  variables: {
                    id: license.Id,
                    input: {
                      TrialEndsAt: dateInputToDatetime(endsAt),
                      LicenseStatusCode: LICENSE_STATUS_TRIAL,
                    },
                  },
                })
              }}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>
    </>
  )
}

export default LicenseExtendMenuItem
